import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import cls from "./JustRegisteredNotEmailVerified.module.scss";
import api from "@/utils/axios";
import { timeAgo } from "@/utils/timeAgo";

const JustRegisteredNotEmailVerified = () => {
  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const fetchMembers = async (p = 1) => {
    try {
      setLoading(true);
      const res = await api.get(
        `/v1/admin/members/members-just-registered-not-email-verified?page=${p}&limit=50`
      );

      setMembers(res.data.data || []);
      setMeta({
        totalPages: res.data.totalPages,
        hasNextPage: res.data.hasNextPage,
        hasPrevPage: res.data.hasPrevPage,
        total: res.data.total,
      });
      setPage(res.data.page);
    } catch (err) {
      console.error("Error loading unverified members:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers(1);
  }, []);

  const handleDelete = async (userId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to mark this user as deleted?"
    );
    if (!confirmDelete) return;

    try {
      setDeletingId(userId);
      await api.patch(`/v1/admin/members/${userId}/delete`);

      // Optimistically remove from list
      setMembers((prev) => prev.filter((m) => m.id !== userId));
      setMeta((prev) => ({
        ...prev,
        total: prev.total > 0 ? prev.total - 1 : 0,
      }));
    } catch (err) {
      console.error("Error deleting user:", err);
      alert("Something went wrong while deleting the user.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className={cls.container}>
      <div className={cls.headerRow}>
        <button
          type="button"
          className={cls.backBtn}
          onClick={() => navigate("/admin/members")}
        >
          ← Back to Members
        </button>
        <div className={cls.titleBlock}>
          <h2 className={cls.title}>Just Registered (Not Email Verified)</h2>
          <p className={cls.subtitle}>
            Active users with <strong>role = member</strong> and{" "}
            <strong>emailVerifiedAt = null</strong>.
          </p>
        </div>
      </div>

      <div className={cls.metaBar}>
        <span className={cls.chip}>
          Total: <strong>{meta.total}</strong>
        </span>
        <span className={cls.chipSoft}>
          Page {page} / {meta.totalPages || 1}
        </span>
      </div>

      {loading ? (
        <div className={cls.loading}>
          <span className={cls.spinner} />
          <span>Loading users...</span>
        </div>
      ) : members.length === 0 ? (
        <div className={cls.empty}>
          <p>No just-registered members found without email verification ✨</p>
          <p className={cls.emptySub}>
            Everyone here has either verified or moved forward in the funnel.
          </p>
        </div>
      ) : (
        <>
          <div className={cls.tableWrap}>
            <table className={cls.table}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Joined</th>
                  <th className={cls.actionsCol}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((u, index) => (
                  <tr key={u.id}>
                    <td>{(page - 1) * 50 + index + 1}</td>
                    <td className={cls.usernameCell}>
                      <span className={cls.username}>@{u.username}</span>
                    </td>
                    <td className={cls.emailCell}>{u.email}</td>
                    <td className={cls.dateCell}>{timeAgo(u.joinedAt)}</td>
                    <td className={cls.actionsCell}>
                      <button
                        type="button"
                        className={cls.deleteBtn}
                        disabled={deletingId === u.id}
                        onClick={() => handleDelete(u.id)}
                      >
                        {deletingId === u.id ? "Deleting..." : "Delete user"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={cls.pagination}>
            <button
              type="button"
              disabled={!meta.hasPrevPage || loading}
              onClick={() => fetchMembers(page - 1)}
            >
              ← Prev
            </button>
            <span>
              Page {page} / {meta.totalPages || 1}
            </span>
            <button
              type="button"
              disabled={!meta.hasNextPage || loading}
              onClick={() => fetchMembers(page + 1)}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default JustRegisteredNotEmailVerified;
